import React, { useState, useEffect } from 'react';
import { Editor } from '@monaco-editor/react';
import Layout from '../components/Layout';
import { adminApi } from '../lib/api';
import type { HomeContent, Section, HeaderConfig } from '../types/content';
import { Plus, Save } from 'lucide-react';
import { Card, PageHeader, Button, Spinner } from '../components/ui';

const DEFAULT_CONTENT: HomeContent = {
  header: {
    title: 'My Lead Syncer',
    subtitle: 'Seamlessly Sync Your ClickFunnels Leads',
    primaryColor: '#4F46E5',
    secondaryColor: '#10B981',
    fontFamily: 'Inter',
    headingFont: 'Inter',
  },
  sections: [
    {
      id: 'hero',
      title: 'Hero Section',
      content:
        '<div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20"><div class="text-center"><h1 class="text-4xl tracking-tight font-extrabold text-gray-900 sm:text-5xl md:text-6xl"><span class="block">Seamlessly Sync Your</span><span class="block text-indigo-600">ClickFunnels Leads</span></h1></div></div>',
      order: 0,
    },
  ],
  updatedAt: Date.now(),
  updatedBy: 'system',
};

export default function AdminHomepage() {
  const [content, setContent] = useState<HomeContent | null>(null);
  const [selectedSection, setSelectedSection] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadContent();
  }, []);

  const loadContent = async () => {
    try {
      const response = await adminApi.getContent();
      if (response.success && response.data) {
        const homepageItem = response.data.find((item) => item.key === 'homepage');
        if (homepageItem) {
          try {
            const parsed = JSON.parse(homepageItem.value) as HomeContent;
            setContent(parsed);
            if (parsed.sections.length > 0) {
              setSelectedSection(parsed.sections[0].id);
            }
          } catch {
            setContent(DEFAULT_CONTENT);
            if (DEFAULT_CONTENT.sections.length > 0) {
              setSelectedSection(DEFAULT_CONTENT.sections[0].id);
            }
          }
        } else {
          setContent(DEFAULT_CONTENT);
          if (DEFAULT_CONTENT.sections.length > 0) {
            setSelectedSection(DEFAULT_CONTENT.sections[0].id);
          }
        }
      } else {
        setContent(DEFAULT_CONTENT);
        if (DEFAULT_CONTENT.sections.length > 0) {
          setSelectedSection(DEFAULT_CONTENT.sections[0].id);
        }
      }
    } catch (error) {
      console.error('Error loading content:', error);
      setContent(DEFAULT_CONTENT);
      if (DEFAULT_CONTENT.sections.length > 0) {
        setSelectedSection(DEFAULT_CONTENT.sections[0].id);
      }
    } finally {
      setLoading(false);
    }
  };

  const saveContent = async (updatedContent: HomeContent) => {
    setSaving(true);
    try {
      const contentValue = JSON.stringify({
        ...updatedContent,
        updatedAt: Date.now(),
      });
      await adminApi.updateContent('homepage', contentValue);
    } catch (error) {
      console.error('Error saving content:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSection = async () => {
    if (!content || !selectedSection) return;

    const section = content.sections.find((s) => s.id === selectedSection);
    if (!section) return;

    setSaving(true);
    try {
      await saveContent(content);
      // Show success feedback
      const button = document.querySelector('[data-save-button]');
      if (button) button.textContent = 'Saved!';
      setTimeout(() => {
        if (button) button.textContent = 'Save Changes';
      }, 2000);
    } catch (error) {
      console.error('Error saving section:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleAddSection = async () => {
    if (!content) return;

    const newSection: Section = {
      id: crypto.randomUUID(),
      title: 'New Section',
      content: '<div class="py-12"><h2>New Section</h2></div>',
      order: content.sections.length,
    };

    const updatedContent = {
      ...content,
      sections: [...content.sections, newSection],
    };

    setContent(updatedContent);
    setSelectedSection(newSection.id);
    await saveContent(updatedContent);
  };

  const handleHeaderChange = async (updates: Partial<HeaderConfig>) => {
    if (!content) return;

    const updatedContent = {
      ...content,
      header: { ...content.header, ...updates },
    };

    setContent(updatedContent);
    await saveContent(updatedContent);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Spinner />
        </div>
      </Layout>
    );
  }

  if (!content) return null;

  return (
    <Layout>
      <PageHeader eyebrow="Admin" title="Homepage Editor" description="Configure the public homepage theme, header, and content sections." />
      <Card>
        <div className="p-6 border-b border-line space-y-4">
          {/* Theme Settings */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="ds-label">Primary Color</label>
              <div className="flex gap-2">
                <input
                  type="color"
                  value={content.header.primaryColor || '#4F46E5'}
                  onChange={(e) =>
                    handleHeaderChange({ primaryColor: e.target.value })
                  }
                  className="h-10 w-20 rounded-[11px] border border-line bg-surface"
                />
                <input
                  type="text"
                  value={content.header.primaryColor || '#4F46E5'}
                  onChange={(e) =>
                    handleHeaderChange({ primaryColor: e.target.value })
                  }
                  className="ds-input font-mono"
                  placeholder="#4F46E5"
                />
              </div>
            </div>

            <div>
              <label className="ds-label">Secondary Color</label>
              <div className="flex gap-2">
                <input
                  type="color"
                  value={content.header.secondaryColor || '#10B981'}
                  onChange={(e) =>
                    handleHeaderChange({ secondaryColor: e.target.value })
                  }
                  className="h-10 w-20 rounded-[11px] border border-line bg-surface"
                />
                <input
                  type="text"
                  value={content.header.secondaryColor || '#10B981'}
                  onChange={(e) =>
                    handleHeaderChange({ secondaryColor: e.target.value })
                  }
                  className="ds-input font-mono"
                  placeholder="#10B981"
                />
              </div>
            </div>

            <div>
              <label className="ds-label">Body Font</label>
              <select
                value={content.header.fontFamily || 'Inter'}
                onChange={(e) =>
                  handleHeaderChange({ fontFamily: e.target.value })
                }
                className="ds-input"
              >
                <option value="Inter">Inter</option>
                <option value="Roboto">Roboto</option>
                <option value="Open Sans">Open Sans</option>
                <option value="Poppins">Poppins</option>
                <option value="Lato">Lato</option>
              </select>
            </div>

            <div>
              <label className="ds-label">Heading Font</label>
              <select
                value={content.header.headingFont || 'Inter'}
                onChange={(e) =>
                  handleHeaderChange({ headingFont: e.target.value })
                }
                className="ds-input"
              >
                <option value="Inter">Inter</option>
                <option value="Roboto">Roboto</option>
                <option value="Open Sans">Open Sans</option>
                <option value="Poppins">Poppins</option>
                <option value="Lato">Lato</option>
              </select>
            </div>
          </div>
        </div>

        <div className="p-6">
          {/* Header Configuration */}
          <div className="mb-8">
            <h3 className="font-display text-base font-semibold text-ink mb-4">
              Header Settings
            </h3>
            <div className="space-y-4">
              <div>
                <label className="ds-label">Site Title</label>
                <input
                  type="text"
                  value={content.header.title}
                  onChange={(e) => handleHeaderChange({ title: e.target.value })}
                  className="ds-input"
                />
              </div>
              <div>
                <label className="ds-label">Subtitle</label>
                <input
                  type="text"
                  value={content.header.subtitle || ''}
                  onChange={(e) =>
                    handleHeaderChange({ subtitle: e.target.value })
                  }
                  className="ds-input"
                />
              </div>
              <div>
                <label className="ds-label">Logo URL</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={content.header.logo || ''}
                    onChange={(e) => handleHeaderChange({ logo: e.target.value })}
                    placeholder="https://example.com/logo.png"
                    className="ds-input font-mono"
                  />
                  {content.header.logo && (
                    <img
                      src={content.header.logo}
                      alt="Logo preview"
                      className="h-10 w-10 object-contain"
                    />
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Section Editor */}
          <div className="grid grid-cols-4 gap-6">
            <div className="col-span-1 space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="font-display text-base font-semibold text-ink">Sections</h3>
                <button
                  onClick={handleAddSection}
                  className="p-2 text-accent-ink hover:bg-accent-soft rounded-[10px]"
                >
                  <Plus className="w-5 h-5" />
                </button>
              </div>
              <div className="space-y-2">
                {content.sections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => setSelectedSection(section.id)}
                    className={`w-full text-left px-4 py-2 rounded-[10px] text-[13px] transition-colors ${
                      selectedSection === section.id
                        ? 'bg-accent-soft text-accent-ink font-medium'
                        : 'text-ink-2 hover:bg-surface-2'
                    }`}
                  >
                    {section.title}
                  </button>
                ))}
              </div>
            </div>

            <div className="col-span-3">
              {selectedSection && (
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <input
                      type="text"
                      value={
                        content.sections.find((s) => s.id === selectedSection)
                          ?.title || ''
                      }
                      onChange={(e) => {
                        const newSections = content.sections.map((s) =>
                          s.id === selectedSection
                            ? { ...s, title: e.target.value }
                            : s
                        );
                        setContent({ ...content, sections: newSections });
                      }}
                      className="ds-input font-display font-medium text-base w-auto"
                    />
                    <div className="flex gap-2">
                      <Button
                        onClick={handleSaveSection}
                        disabled={saving}
                        data-save-button
                        icon={Save}
                      >
                        {saving ? 'Saving...' : 'Save Changes'}
                      </Button>
                    </div>
                  </div>

                  <Editor
                    height="500px"
                    defaultLanguage="html"
                    value={
                      content.sections.find((s) => s.id === selectedSection)
                        ?.content || ''
                    }
                    onChange={(value) => {
                      const newSections = content.sections.map((s) =>
                        s.id === selectedSection
                          ? { ...s, content: value || '' }
                          : s
                      );
                      setContent({ ...content, sections: newSections });
                    }}
                    options={{
                      minimap: { enabled: false },
                      wordWrap: 'on',
                      lineNumbers: 'on',
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </Card>
    </Layout>
  );
}
